/*
 * Sharing a page or a record from the app (2026-10-19): a link someone with
 * access can open, the system share sheet where the device has one (phones),
 * and the clipboard everywhere else. Nothing here sends anything anywhere by
 * itself: the person chooses where it goes.
 */

/** An absolute link to `path` on this app (for a share, an invitation). */
export function appLink(path) {
  const origin = typeof window !== 'undefined' && window.location ? window.location.origin : '';
  return `${origin}${path.startsWith('/') ? path : `/${path}`}`;
}

/** True where the device has a share sheet (navigator.share). */
export function canShareNatively() {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

/** Copy `text`; resolves true, or false where the clipboard is refused. */
export async function copyText(text) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the older way
  }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand && document.execCommand('copy');
    area.remove();
    return !!ok;
  } catch {
    return false;
  }
}

/**
 * The share sheet with {title, text, url}; resolves 'shared', 'cancelled'
 * (the person closed it) or 'unavailable'.
 */
export async function shareNatively({ title, text, url }) {
  if (!canShareNatively()) return 'unavailable';
  try {
    await navigator.share({ title, text, url });
    return 'shared';
  } catch (e) {
    return e?.name === 'AbortError' ? 'cancelled' : 'unavailable';
  }
}

/** A plain-text summary: a title line, then "label: value" for each value given. */
export function summaryText(title, rows, url) {
  const lines = [title, ''];
  rows.filter(([, value]) => value !== null && value !== undefined && value !== '')
    .forEach(([label, value]) => lines.push(`${label}: ${value}`));
  if (url) lines.push('', url);
  return `${lines.join('\n').trim()}\n`;
}
